<template>
  <BaseVeeForm :action="updatePipelineStages" v-slot="{ isPending, isRejected, invalid }">
    <BaseCard v-if="internalPipelineStages">
      <BaseCardBody>
        <div>
          <h3 class="text-lg leading-6 font-medium text-gray-900">Pipeline stages</h3>
          <p class="max-w-2xl text-sm text-gray-500">The pipeline's stages</p>
        </div>
        <div class="mt-6">
          <div class="bg-white space-y-6">
            <ul
              v-if="internalPipelineStages.open"
              class="border border-gray-300 overflow-hidden rounded-md"
            >
              <PipelineStage
                :pipeline-stage="internalPipelineStages.open"
                @update="updatePipelineStage('open', $event)"
              />
            </ul>
            <draggable
              v-model="internalPipelineStages.custom"
              group="pipelineStages"
              tag="ul"
              handle=".handle"
              filter=".filter"
              class="divide-y divide-gray-300 border border-gray-300 overflow-hidden rounded-md"
            >
              <PipelineStage
                v-for="(pipelineStage, idx) in internalPipelineStages.custom"
                :key="pipelineStage.id || idx"
                :pipeline-stage="pipelineStage"
                @update="updateCustomPipelineStage(idx, $event)"
                @remove="removePipelineStage(idx)"
              />
            </draggable>

            <div class="mt-4">
              <PipelineStageAdd @add-pipeline-stage="addPipelineStage" />
            </div>

            <ul class="divide-y divide-gray-300 border border-gray-300 overflow-hidden rounded-md">
              <PipelineStage
                v-if="internalPipelineStages.won"
                :pipeline-stage="internalPipelineStages.won"
                @update="updatePipelineStage('won', $event)"
              />
              <PipelineStage
                v-if="internalPipelineStages.lost"
                :pipeline-stage="internalPipelineStages.lost"
                @update="updatePipelineStage('lost', $event)"
              />
              <PipelineStage
                v-if="internalPipelineStages.closed"
                :pipeline-stage="internalPipelineStages.closed"
                @update="updatePipelineStage('closed', $event)"
              />
            </ul>
          </div>
        </div>
      </BaseCardBody>
      <BaseCardFooter gray>
        <BaseButton primary :disabled="isPending || invalid || isRejected">
          Update pipeline stages
        </BaseButton>
      </BaseCardFooter>
    </BaseCard>
  </BaseVeeForm>
</template>

<script>
import { pick, sortBy, omit } from 'lodash-es';
import Draggable from 'vuedraggable';
import PipelineStage from './PipelineStage.vue';
import PipelineStageAdd from './PipelineStageAdd.vue';
import colors from './colors';

export default {
  components: {
    PipelineStage,
    PipelineStageAdd,
    Draggable,
  },
  props: {
    pipelineId: {
      type: String,
      required: true,
    },
    pipelineStages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      internalPipelineStages: null,
    };
  },
  watch: {
    pipelineStages: 'setInternalPipelineStages',
  },
  created() {
    this.setInternalPipelineStages(this.pipelineStages);
  },
  methods: {
    setInternalPipelineStages(pipelineStages) {
      this.internalPipelineStages = {
        open: null,
        custom: [],
        won: null,
        lost: null,
        closed: null,
      };
      sortBy(pipelineStages, 'order').forEach((pipelineStage) => {
        const attributes = pick(pipelineStage, ['id', 'name', 'type', 'description', 'color']);

        if (!attributes.color) {
          Object.assign(attributes, { color: colors[0] });
        }

        if (attributes.type !== 'custom') {
          this.internalPipelineStages[attributes.type] = attributes;
        } else {
          this.internalPipelineStages.custom.push(attributes);
        }
      });
    },
    addPipelineStage(name) {
      this.internalPipelineStages.custom.push({
        name,
        type: 'custom',
        color: colors[0],
        description: '',
      });
    },
    updatePipelineStage(type, newPipelineStage) {
      this.internalPipelineStages[type] = {
        ...this.internalPipelineStages[type],
        ...newPipelineStage,
      };
    },
    updateCustomPipelineStage(index, newPipelineStage) {
      const { [index]: pipelineStage, ...rest } = this.internalPipelineStages.custom;

      this.internalPipelineStages.custom = Object.values({
        [index]: {
          ...pipelineStage,
          ...newPipelineStage,
        },
        ...rest,
      });
    },
    removePipelineStage(index) {
      this.internalPipelineStages.custom = Object.values({
        ...omit(this.internalPipelineStages.custom, [index]),
      });
    },
    async updatePipelineStages() {
      try {
        await this.$store.dispatch('ui/pipelines/update', {
          pipelineUuid: this.pipelineId,
          formData: {
            pipelineStages: [
              this.internalPipelineStages.open,
              ...this.internalPipelineStages.custom,
              this.internalPipelineStages.won,
              this.internalPipelineStages.lost,
              this.internalPipelineStages.closed,
            ].filter(Boolean),
          },
        });

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
  },
};
</script>
